import React from 'react';
import MainPrintLogo from './MainPrintLogo';
import { printPalette, printTypography } from './printPalette';
import { sharedPrintStyles, basePrintContainerStyle } from './printStyles';

interface StatRow {
  code: string;
  name: string;
  total: number;
  collect: number;
  prepaid: number;
  car: number;
  van: number;
  bigVan: number;
  truck: number;
}

interface VoyageStatisticsPrintProps {
  voyageCode: string;
  vesselName: string;
  date: string;
  viewType: 'shippers' | 'ports';
  data: StatRow[];
}

const VoyageStatisticsPrint: React.FC<VoyageStatisticsPrintProps> = ({
  voyageCode,
  vesselName,
  date,
  viewType,
  data,
}) => {
  const totals = data.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      collect: acc.collect + row.collect,
      prepaid: acc.prepaid + row.prepaid,
      car: acc.car + row.car,
      van: acc.van + row.van,
      bigVan: acc.bigVan + row.bigVan,
      truck: acc.truck + row.truck,
    }),
    { total: 0, collect: 0, prepaid: 0, car: 0, van: 0, bigVan: 0, truck: 0 }
  );

  return (
    <>
      <style>{voyagePrintStyles}</style>
      <div style={styles.container} className="voyage-statistics">
        <MainPrintLogo />

        <div style={styles.infoSection}>
          <div>
            <p><strong>Voyage:</strong> {voyageCode}</p>
            <p><strong>Vessel:</strong> {vesselName}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p><strong>Date:</strong> {date}</p>
          </div>
        </div>

        <h2 style={styles.title}>VOYAGE STATISTICS - BY {viewType === 'shippers' ? 'SHIPPERS' : 'PORTS'}</h2>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Voyage</th>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>{viewType === 'shippers' ? 'Shipper' : 'Port'}</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Collect</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Prepaid</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Car (P)</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Van (B)</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>BigVan (G)</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Truck (L)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                <td style={styles.td}>{voyageCode}</td>
                <td style={styles.td}>{row.code}</td>
                <td style={styles.td}>{row.name}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.total}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.collect}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.prepaid}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.car}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.van}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.bigVan}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{row.truck}</td>
              </tr>
            ))}
            <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <td style={styles.td} colSpan={3}>Total</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.total}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.collect}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.prepaid}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.car}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.van}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.bigVan}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{totals.truck}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

const voyagePrintStyles = sharedPrintStyles;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    ...basePrintContainerStyle,
    fontSize: `${printTypography.baseSize}px`,
    padding: '20px',
  },
  infoSection: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    fontSize: `${printTypography.infoSize}px`,
    color: printPalette.text,
  },
  title: {
    fontSize: `${printTypography.subtitleSize}px`,
    fontWeight: 'bold',
    margin: '0 0 15px 0',
    color: printPalette.primaryDark,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '11px',
    color: printPalette.text,
  },
  th: {
    borderTop: `2px solid ${printPalette.primaryDark}`,
    borderBottom: `2px solid ${printPalette.primaryDark}`,
    padding: '6px 4px',
    textAlign: 'left',
    fontWeight: 'bold',
    backgroundColor: printPalette.headerBg,
    color: printPalette.primaryDark,
  },
  td: {
    padding: '5px 4px',
    borderBottom: `1px solid ${printPalette.borderLight}`,
    color: printPalette.text,
  },
};

export default VoyageStatisticsPrint;

